import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Modal, Text } from '@teals/ui-web';
import {
  useUpdateAgentConfigMutation,
  useUpdateAgentDataSourcesMutation,
  useGetDataSourcesQuery,
  useGetOrgModelsQuery,
  type Agent,
  type AgentDataSourceRef,
  type AgentDetail,
  type DataSource,
} from '../../store/api';
import { useAuth } from '../../hooks/useAuth';
import {
  ConfigureFleetPanel,
  ConfigureMembershipPanel,
  isManagementAgent,
} from './AgentSystemPanels';
import {
  DS_TYPE_LABELS,
  DS_TYPE_BADGE_VARIANT,
} from '../DataSources/constants';
import type { DsType } from '../../store/api/dataSourcesApi';
import {
  formatModelOptionLabel,
  groupModelsByFamily,
  matchesModelSearch,
} from '../../utils/modelDisplay';
import type { OrgModel } from '../../store/api/orgModelsApi';
import BuilderChat from '../AgentBuilder/BuilderChat';
import {
  type AgentConfigSnapshot,
  type ProposedConfig,
} from '../AgentBuilder/builderHelpers';

type EffortLevel = 'low' | 'medium' | 'high';

interface AgentConfig extends AgentConfigSnapshot {}

const FALLBACK_MODEL_OPTIONS: OrgModel[] = [
  {
    productId: 'gpt-4o',
    displayName: 'GPT-4o',
    provider: 'openai',
    lab: 'openai',
    route: 'openai',
    family: 'gpt',
    chatCapable: true,
    capabilities: [],
    enabled: true,
  },
  {
    productId: 'gpt-4o-mini',
    displayName: 'GPT-4o Mini',
    provider: 'openai',
    lab: 'openai',
    route: 'openai',
    family: 'gpt',
    chatCapable: true,
    capabilities: [],
    enabled: true,
  },
  {
    productId: 'claude-sonnet-4-6',
    displayName: 'Claude Sonnet 4.6',
    provider: 'bedrock',
    lab: 'anthropic',
    route: 'bedrock',
    family: 'claude',
    chatCapable: true,
    capabilities: [],
    enabled: true,
  },
  {
    productId: 'claude-haiku-4-5',
    displayName: 'Claude Haiku 4.5',
    provider: 'bedrock',
    lab: 'anthropic',
    route: 'bedrock',
    family: 'claude',
    chatCapable: true,
    capabilities: [],
    enabled: true,
  },
  {
    productId: 'nova-lite',
    displayName: 'Nova Lite',
    provider: 'bedrock',
    lab: 'amazon',
    route: 'bedrock',
    family: 'nova',
    chatCapable: true,
    capabilities: [],
    enabled: true,
  },
  {
    productId: 'nova-pro',
    displayName: 'Nova Pro',
    provider: 'bedrock',
    lab: 'amazon',
    route: 'bedrock',
    family: 'nova',
    chatCapable: true,
    capabilities: [],
    enabled: true,
  },
];


const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 520px;

  @media (max-width: 1023px) {
    min-height: min(520px, 50dvh);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;


const PreviewCard = styled(Card)`
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1023px) {
    position: static;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BlockHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 0.8125rem;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const FieldName = styled.span`
  color: var(--text-muted);
  flex-shrink: 0;
  padding-top: 0.35rem;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  align-items: flex-end;
`;

const ConfigInput = styled.input`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  font-family: inherit;
  max-width: 14rem;
  width: 100%;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfigSelect = styled.select`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  font-family: inherit;
  max-width: 14rem;
  width: 100%;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfigTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  font-size: 0.8125rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--text-primary);
  background: var(--bg-elevated, #f8fafc);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem;
  resize: vertical;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RemovableChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-elevated, #f8fafc);
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  line-height: 1;

  &:hover:not(:disabled) {
    color: var(--danger, #dc2626);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-of-type {
    border-bottom: none;
  }
`;

const DsDragTray = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 2px dashed color-mix(in srgb, var(--accent) 35%, var(--border));
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent) 6%, var(--bg-elevated, #f8fafc));
`;

const DsDragLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--accent);
  margin-bottom: 0.5rem;
`;

const DsChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-primary);
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Grip = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0.55;

  span {
    display: block;
    width: 10px;
    height: 2px;
    border-radius: 1px;
    background: var(--text-muted);
  }
`;

const ComingSoon = styled.div`
  opacity: 0.65;
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: 0.75rem;
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
`;

const DraftBanner = styled.div`
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--warning, #d97706);
  background: color-mix(in srgb, var(--warning, #d97706) 10%, transparent);
  font-size: 0.875rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const Helper = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 280px;
  overflow: auto;
  margin: 1rem 0;
`;

const PickerRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
`;

function buildConfigFromAgent(agent: AgentDetail): AgentConfig {
  const rawEffort =
    (agent.config as Record<string, unknown> | undefined)?.effort ??
    (agent as unknown as Record<string, unknown>).effort;
  const effort: EffortLevel =
    rawEffort === 'low' || rawEffort === 'high' ? rawEffort : 'medium';
  return {
    systemPrompt: agent.config?.systemPrompt || '',
    model: agent.config?.model || agent.llmModel || 'gpt-4o',
    temperature: agent.config?.temperature ?? 0.7,
    maxTokens: agent.config?.maxTokens ?? 2000,
    responseMode: agent.config?.responseMode || 'sync',
    recommendedModels:
      agent.recommendedModels ||
      agent.config?.recommendedModels ||
      [],
    effort,
  };
}

function resolveAssociatedSources(
  agent: AgentDetail,
  catalog: DataSource[] | undefined,
): AgentDataSourceRef[] {
  if (agent.dataSources?.length) return agent.dataSources;
  if (!catalog?.length) return [];
  return catalog
    .filter(
      (ds) =>
        ds.agentIds.includes(agent.id) ||
        ds.agentIds.includes(agent.agentName),
    )
    .map((ds) => ({
      dataSourceId: ds.dataSourceId,
      name: ds.name,
      dsType: ds.dsType,
      status: ds.status,
    }));
}

function dsTypeLabel(type?: string): string {
  if (!type) return 'Data source';
  return DS_TYPE_LABELS[type as DsType] || type;
}

function greetingFor(
  agent: AgentDetail,
  config: AgentConfig,
  dsCount: number,
  fleetCount: number,
  readOnly: boolean,
  needsVersion: boolean,
): string {
  if (readOnly) {
    return `I'm the configuration assistant for ${agent.name}. This agent is read-only — I can explain settings but cannot apply changes.`;
  }
  const typeLabel = isManagementAgent(agent) ? 'Management' : 'Agent';
  const status = needsVersion
    ? 'Published (create a version to edit)'
    : agent.hasOpenDraft
      ? `Draft ${agent.draftVersion || agent.version}`
      : agent.versionStatus;
  const fleetBit = isManagementAgent(agent)
    ? `, fleet of ${fleetCount}`
    : agent.membership
      ? `, part of ${agent.membership.parentAgentName}`
      : '';
  return (
    `I'm the builder for ${agent.name} (${typeLabel} · ${status}).\n\n` +
    `Current config: default ${config.model}, ${config.recommendedModels.length} recommended models, ` +
    `${dsCount} data source${dsCount === 1 ? '' : 's'}${fleetBit}.\n\n` +
    `Drag a data source chip into the input to reference it, or describe a change. ` +
    `I will never ask for API secrets.`
  );
}

export interface ConfigureTabProps {
  agent: AgentDetail;
  catalog: Agent[];
  readOnly: boolean;
  onCreateVersion?: () => void;
}

const ConfigureTab: React.FC<ConfigureTabProps> = ({
  agent,
  catalog,
  readOnly,
  onCreateVersion,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const management = isManagementAgent(agent);
  const needsVersion =
    !readOnly &&
    !agent.hasOpenDraft &&
    agent.versionStatus === 'published';
  const editsEnabled = !readOnly && !needsVersion;

  const { data: orgDataSources } = useGetDataSourcesQuery();
  const { data: orgModels } = useGetOrgModelsQuery(orgId!, { skip: !orgId });
  const associated = useMemo(
    () => resolveAssociatedSources(agent, orgDataSources),
    [agent, orgDataSources],
  );

  const [config, setConfig] = useState<AgentConfig>(() =>
    buildConfigFromAgent(agent),
  );
  const [savedConfig, setSavedConfig] = useState<AgentConfig>(() =>
    buildConfigFromAgent(agent),
  );
  const [fleetNames, setFleetNames] = useState<string[]>(
    agent.fleet?.agents?.map((m) => m.agentName) || [],
  );
  const [dataSources, setDataSources] =
    useState<AgentDataSourceRef[]>(associated);
  const [savedDataSourceIds, setSavedDataSourceIds] = useState<string[]>(
    associated.map((d) => d.dataSourceId),
  );
  const [pending, setPending] = useState<ProposedConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [associateOpen, setAssociateOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState<string[]>([]);
  const [removeConfirm, setRemoveConfirm] = useState<AgentDataSourceRef | null>(
    null,
  );

  const [updateConfig] = useUpdateAgentConfigMutation();
  const [updateAgentDataSources] = useUpdateAgentDataSourcesMutation();

  useEffect(() => {
    const next = buildConfigFromAgent(agent);
    setConfig(next);
    setSavedConfig(next);
    setFleetNames(agent.fleet?.agents?.map((m) => m.agentName) || []);
    const nextDs = resolveAssociatedSources(agent, orgDataSources);
    setDataSources(nextDs);
    setSavedDataSourceIds(nextDs.map((d) => d.dataSourceId));
    // Sync from agent / org catalog identity — avoid depending on full object refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agent.id,
    agent.config?.systemPrompt,
    agent.config?.model,
    agent.config?.temperature,
    agent.config?.maxTokens,
    agent.config?.responseMode,
    agent.llmModel,
    agent.hasOpenDraft,
    agent.versionStatus,
    JSON.stringify(agent.recommendedModels),
    JSON.stringify(agent.dataSources),
    JSON.stringify(agent.fleet?.agents?.map((m) => m.agentName)),
    JSON.stringify(orgDataSources?.map((d) => d.dataSourceId)),
  ]);

  const dirty = useMemo(() => {
    const cfgDirty =
      config.systemPrompt !== savedConfig.systemPrompt ||
      config.model !== savedConfig.model ||
      config.temperature !== savedConfig.temperature ||
      config.maxTokens !== savedConfig.maxTokens ||
      config.responseMode !== savedConfig.responseMode ||
      config.effort !== savedConfig.effort ||
      [...config.recommendedModels].sort().join(',') !==
        [...savedConfig.recommendedModels].sort().join(',');
    const dsDirty =
      [...dataSources.map((d) => d.dataSourceId)].sort().join(',') !==
      [...savedDataSourceIds].sort().join(',');
    return cfgDirty || dsDirty;
  }, [config, savedConfig, dataSources, savedDataSourceIds]);

  const seedChips = useMemo(() => {
    if (readOnly || needsVersion) return [] as string[];
    const chips = [
      'Change default model',
      'Set recommended models',
      'Associate data source',
      'Create data source',
      'Update system prompt',
    ];
    if (management) {
      chips.unshift('Add fleet member');
      if (fleetNames.length) {
        chips.unshift(`Remove ${fleetNames[fleetNames.length - 1]} from fleet`);
      }
    }
    return chips;
  }, [readOnly, needsVersion, management, fleetNames]);

  const preview = useMemo(() => {
    return {
      systemPrompt: pending?.systemPrompt ?? config.systemPrompt,
      model: pending?.model ?? config.model,
      temperature: pending?.temperature ?? config.temperature,
      maxTokens: pending?.maxTokens ?? config.maxTokens,
      responseMode: pending?.responseMode ?? config.responseMode,
      recommendedModels:
        pending?.recommendedModels ?? config.recommendedModels,
      dataSources: pending?.dataSources ?? dataSources.map((d) => d.name),
      fleet: pending?.fleet ?? fleetNames,
      agentType: pending?.agentType ?? agent.agentType,
      orchestrationMode:
        pending?.orchestrationMode ?? agent.fleet?.orchestrationMode,
    };
  }, [pending, config, dataSources, fleetNames, agent.agentType, agent.fleet]);

  const promptContext = useMemo(
    () => ({
      mode: 'configure',
      edit_mode: true,
      read_only: readOnly || needsVersion,
      target_agent: {
        id: agent.id,
        agent_name: agent.agentName,
        name: agent.name,
        agent_type: agent.agentType,
      },
      current_config: {
        system_prompt: config.systemPrompt,
        llm_model: config.model,
        settings: {
          temperature: config.temperature,
          max_tokens: config.maxTokens,
          response_mode: config.responseMode,
        },
        recommended_models: config.recommendedModels,
        data_sources: dataSources.map((d) => ({
          data_source_id: d.dataSourceId,
          name: d.name,
        })),
        agent_type: agent.agentType,
        fleet: { agents: fleetNames },
        orchestration: {
          mode: agent.fleet?.orchestrationMode || 'fan_out',
        },
      },
      catalog_agents: catalog
        .filter((a) => a.id !== agent.id)
        .slice(0, 40)
        .map((a) => ({
          agent_name: a.agentName,
          name: a.name,
          agent_type: a.agentType,
        })),
    }),
    [
      agent,
      catalog,
      config,
      dataSources,
      fleetNames,
      readOnly,
      needsVersion,
    ],
  );

  const handleChipSpecial = (chip: string): boolean => {
    if (chip.toLowerCase().includes('create data source')) {
      navigate(
        `/data-sources?create=1&returnTo=${encodeURIComponent(`/agents/${agent.id}`)}&tab=configure`,
      );
      return true;
    }
    if (chip.toLowerCase().includes('associate data source')) {
      setAssociateOpen(true);
      setPickerSelected(dataSources.map((d) => d.dataSourceId));
      return true;
    }
    return false;
  };

  const persistConfig = async (
    next: AgentConfig,
    nextFleet: string[],
    extra?: Record<string, unknown>,
  ) => {
    await updateConfig({
      id: agent.id,
      config: {
        system_prompt: next.systemPrompt,
        model: next.model,
        temperature: next.temperature,
        max_tokens: next.maxTokens,
        response_mode: next.responseMode,
        recommended_models: next.recommendedModels,
        effort: next.effort,
        ...(nextFleet.length || management
          ? {
              fleet: { agents: nextFleet },
              agent_type: 'management',
              orchestration: {
                mode:
                  (extra?.orchestrationMode as string) ||
                  agent.fleet?.orchestrationMode ||
                  'fan_out',
              },
            }
          : {}),
      },
    }).unwrap();
  };

  const persistDataSources = async (ids: string[]) => {
    await updateAgentDataSources({
      id: agent.id,
      dataSourceIds: ids,
    }).unwrap();
  };

  const handleAccept = async ({
    proposed,
    conversationId,
  }: {
    proposed: ProposedConfig;
    conversationId?: string;
  }) => {
    if (!editsEnabled) return;
    const next: AgentConfig = {
      systemPrompt: proposed.systemPrompt ?? config.systemPrompt,
      model: proposed.model ?? config.model,
      temperature: proposed.temperature ?? config.temperature,
      maxTokens: proposed.maxTokens ?? config.maxTokens,
      responseMode: proposed.responseMode ?? config.responseMode,
      recommendedModels: proposed.recommendedModels ?? config.recommendedModels,
      effort: proposed.effort ?? config.effort,
    };
    const nextFleet = proposed.fleet ?? fleetNames;
    await updateConfig({
      id: agent.id,
      apply: true,
      conversationId,
      config: {
        system_prompt: next.systemPrompt,
        model: next.model,
        temperature: next.temperature,
        max_tokens: next.maxTokens,
        response_mode: next.responseMode,
        recommended_models: next.recommendedModels,
        effort: next.effort,
        ...(proposed.dataSources
          ? { data_sources: proposed.dataSources }
          : {}),
        ...(nextFleet.length || management
          ? {
              fleet: { agents: nextFleet },
              agent_type: proposed.agentType || 'management',
              orchestration: {
                mode:
                  proposed.orchestrationMode ||
                  agent.fleet?.orchestrationMode ||
                  'fan_out',
              },
            }
          : {}),
      },
    }).unwrap();

    let nextDs = dataSources;
    if (proposed.dataSources) {
      const byName = new Map(
        (orgDataSources || []).map((d) => [d.name, d] as const),
      );
      const ids: string[] = [];
      const refs: AgentDataSourceRef[] = [];
      for (const name of proposed.dataSources) {
        const existing = dataSources.find((d) => d.name === name);
        const fromCatalog = byName.get(name);
        if (existing) {
          ids.push(existing.dataSourceId);
          refs.push(existing);
        } else if (fromCatalog) {
          ids.push(fromCatalog.dataSourceId);
          refs.push({
            dataSourceId: fromCatalog.dataSourceId,
            name: fromCatalog.name,
            dsType: fromCatalog.dsType,
            status: fromCatalog.status,
          });
        }
      }
      nextDs = refs;
      setSavedDataSourceIds(ids);
    }

    setConfig(next);
    setSavedConfig(next);
    setFleetNames(nextFleet);
    setDataSources(nextDs);
    setPending(null);
  };

  const handleFleetChange = async (names: string[]) => {
    setFleetNames(names);
    if (!editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      await persistConfig(config, names);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to update fleet',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardEdits = () => {
    setConfig(savedConfig);
    const restored = (orgDataSources || [])
      .filter((d) => savedDataSourceIds.includes(d.dataSourceId))
      .map((d) => ({
        dataSourceId: d.dataSourceId,
        name: d.name,
        dsType: d.dsType,
        status: d.status,
      }));
    if (restored.length > 0 || savedDataSourceIds.length === 0) {
      setDataSources(restored);
      return;
    }
    setDataSources(
      associated.filter((d) => savedDataSourceIds.includes(d.dataSourceId)),
    );
  };

  const handleSaveDraft = async () => {
    if (!editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      await persistConfig(config, fleetNames);
      const ids = dataSources.map((d) => d.dataSourceId);
      await persistDataSources(ids);
      setSavedConfig(config);
      setSavedDataSourceIds(ids);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to save draft',
      );
    } finally {
      setSaving(false);
    }
  };

  const openAgent = (idOrName: string) => {
    navigate(`/agents/${encodeURIComponent(idOrName)}`);
  };

  const availableToAssociate = useMemo(() => {
    const linked = new Set(dataSources.map((d) => d.dataSourceId));
    return (orgDataSources || []).filter((d) => !linked.has(d.dataSourceId));
  }, [orgDataSources, dataSources]);

  const modelCatalogById = useMemo(() => {
    const map = new Map<string, OrgModel>();
    const source = orgModels?.length ? orgModels : FALLBACK_MODEL_OPTIONS;
    for (const m of source) {
      map.set(m.productId, m);
    }
    return map;
  }, [orgModels]);

  const modelOptionsGrouped = useMemo(() => {
    const chatEnabled = orgModels
      ? orgModels.filter((m) => m.enabled && m.chatCapable !== false)
      : FALLBACK_MODEL_OPTIONS;
    const byId = new Map(chatEnabled.map((m) => [m.productId, m]));
    // Keep current config values even if not in org-enabled chat pool
    for (const pid of [config.model, ...config.recommendedModels]) {
      if (!pid || byId.has(pid)) continue;
      const known = modelCatalogById.get(pid);
      byId.set(
        pid,
        known || {
          productId: pid,
          displayName: pid,
          provider: '',
          capabilities: [],
          enabled: true,
          chatCapable: true,
        },
      );
    }
    let list = [...byId.values()];
    if (modelSearch.trim()) {
      list = list.filter((m) => matchesModelSearch(m, modelSearch));
    }
    return groupModelsByFamily(list);
  }, [
    config.model,
    config.recommendedModels,
    orgModels,
    modelCatalogById,
    modelSearch,
  ]);

  const renderModelOptGroups = () =>
    modelOptionsGrouped.map((section) => (
      <optgroup key={section.family} label={section.label}>
        {section.models.map((m) => (
          <option key={m.productId} value={m.productId}>
            {formatModelOptionLabel(m)}
          </option>
        ))}
      </optgroup>
    ));

  return (
    <Layout>
      <EditorPane>
        {needsVersion && (
          <DraftBanner>
            <span>
              Create a new version to edit Live Configuration or apply Agent
              Builder changes.
            </span>
            {onCreateVersion && (
              <Button variant="primary" size="sm" onClick={onCreateVersion}>
                Create New Version
              </Button>
            )}
          </DraftBanner>
        )}

        <BuilderChat
          mode="configure"
          greeting={greetingFor(
            agent,
            config,
            dataSources.length,
            fleetNames.length,
            readOnly,
            needsVersion,
          )}
          subtitle="Describe changes in natural language. Drag a data source into the dropzone — AI proposes diffs you Accept or Discard. Never asks for API secrets."
          placeholder="Drop a data source here or ask to configure…"
          seedChips={seedChips}
          promptContext={promptContext}
          metadataSource="configure"
          editsEnabled={editsEnabled}
          inputDisabled={needsVersion}
          currentConfig={config}
          fleetNames={fleetNames}
          dataSourceNames={dataSources.map((d) => d.name)}
          appliedMessage="Applied to Draft. Publish when you are ready to make this live."
          onAccept={handleAccept}
          onChipClick={handleChipSpecial}
          onPendingChange={setPending}
        />

        {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

        {(management || fleetNames.length > 0) && (
          <ConfigureFleetPanel
            fleet={{
              agents: fleetNames.map((name) => {
                const existing = agent.fleet?.agents.find(
                  (m) => m.agentName === name,
                );
                return (
                  existing || {
                    agentName: name,
                    displayName: name,
                  }
                );
              }),
              orchestrationMode: agent.fleet?.orchestrationMode,
              orchestrationLabel: agent.fleet?.orchestrationLabel,
            }}
            catalog={catalog.filter((a) => a.id !== agent.id)}
            readOnly={!editsEnabled}
            onChange={handleFleetChange}
            onOpenAgent={openAgent}
          />
        )}
        {agent.membership && !management && (
          <ConfigureMembershipPanel
            membership={agent.membership}
            onOpenParent={openAgent}
          />
        )}
      </EditorPane>

      <PreviewCard padding={20} elevation="sm">
        <Toolbar>
          <div>
            <Text variant="h4" style={{ margin: 0 }}>
              Live Configuration
            </Text>
            <Helper>
              Edit fields directly or via Agent Builder.
              {dirty ? ' Unsaved changes.' : ''}
            </Helper>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardEdits}
              disabled={!dirty || saving || !editsEnabled}
            >
              Discard edits
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveDraft}
              disabled={!dirty || saving || !editsEnabled}
            >
              {saving ? 'Saving…' : 'Save to Draft'}
            </Button>
          </div>
        </Toolbar>

        {(management || preview.fleet.length > 0) && (
          <div>
            <BlockHeader>Fleet & orchestration</BlockHeader>
            <FieldRow>
              <FieldName>mode</FieldName>
              <span style={{ fontWeight: 500 }}>
                {preview.orchestrationMode || '—'}
              </span>
            </FieldRow>
            <FieldRow>
              <FieldName>members</FieldName>
              <span style={{ fontWeight: 500, textAlign: 'right' }}>
                {preview.fleet.length ? preview.fleet.join(', ') : 'None'}
              </span>
            </FieldRow>
          </div>
        )}

        <div>
          <BlockHeader>Model Settings</BlockHeader>
          <FieldRow>
            <FieldName>filter</FieldName>
            <ConfigInput
              type="search"
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
              placeholder="Filter models by name, id, lab, route…"
              aria-label="Filter models"
            />
          </FieldRow>
          <FieldRow>
            <FieldName>default_model</FieldName>
            <ConfigSelect
              value={config.model}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({ ...c, model: e.target.value }))
              }
              aria-label="Default model"
            >
              {renderModelOptGroups()}
            </ConfigSelect>
          </FieldRow>
          <FieldRow>
            <FieldName>recommended_models</FieldName>
            <FieldStack>
              <ChipRow>
                {config.recommendedModels.map((m) => (
                  <RemovableChip key={m}>
                    {m}
                    <ChipRemove
                      type="button"
                      aria-label={`Remove ${m}`}
                      disabled={!editsEnabled}
                      onClick={() =>
                        setConfig((c) => ({
                          ...c,
                          recommendedModels: c.recommendedModels.filter(
                            (x) => x !== m,
                          ),
                        }))
                      }
                    >
                      ×
                    </ChipRemove>
                  </RemovableChip>
                ))}
                <ConfigSelect
                  value=""
                  disabled={!editsEnabled}
                  aria-label="Add recommended model"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    setConfig((c) =>
                      c.recommendedModels.includes(value)
                        ? c
                        : {
                            ...c,
                            recommendedModels: [
                              ...c.recommendedModels,
                              value,
                            ],
                          },
                    );
                    e.target.value = '';
                  }}
                  style={{ maxWidth: '14rem' }}
                >
                  <option value="">+ Add…</option>
                  {renderModelOptGroups()}
                </ConfigSelect>
              </ChipRow>
            </FieldStack>
          </FieldRow>
          <Helper>
            Playground and API may pass <code>model</code> to override for a
            single exchange. Recommended list feeds Auto mode. Only chat-capable
            org-enabled models appear here (grouped by family).
          </Helper>
          <FieldRow>
            <FieldName>temperature</FieldName>
            <ConfigInput
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={config.temperature}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  temperature: Number(e.target.value),
                }))
              }
              aria-label="Temperature"
            />
          </FieldRow>
          <FieldRow>
            <FieldName>max_tokens</FieldName>
            <ConfigInput
              type="number"
              min={1}
              step={1}
              value={config.maxTokens}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  maxTokens: Number(e.target.value),
                }))
              }
              aria-label="Max tokens"
            />
          </FieldRow>
          <FieldRow>
            <FieldName>response_mode</FieldName>
            <ConfigSelect
              value={config.responseMode}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({ ...c, responseMode: e.target.value }))
              }
              aria-label="Response mode"
            >
              <option value="async">async</option>
              <option value="sync">sync</option>
            </ConfigSelect>
          </FieldRow>
          <FieldRow>
            <FieldName>effort</FieldName>
            <ConfigSelect
              value={config.effort}
              disabled={!editsEnabled}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  effort: e.target.value as EffortLevel,
                }))
              }
              aria-label="Effort level"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </ConfigSelect>
          </FieldRow>
          <Helper>
            Effort controls reasoning depth. Low is fast/cheap, High uses
            extended thinking when the model supports it.
          </Helper>
        </div>

        <div>
          <BlockHeader>System prompt</BlockHeader>
          <ConfigTextarea
            value={config.systemPrompt}
            disabled={!editsEnabled}
            onChange={(e) =>
              setConfig((c) => ({ ...c, systemPrompt: e.target.value }))
            }
            aria-label="System prompt"
            placeholder="Supports $DataSource:Name directives"
          />
        </div>

        <div>
          <BlockHeader>Data sources</BlockHeader>
          {dataSources.length === 0 ? (
            <Helper>No data sources associated yet.</Helper>
          ) : (
            dataSources.map((ds) => (
              <DsRow key={ds.dataSourceId}>
                <span>
                  {ds.name}{' '}
                  <Badge
                    variant={
                      (DS_TYPE_BADGE_VARIANT[ds.dsType as DsType] as
                        | 'info'
                        | 'success'
                        | 'warning'
                        | 'neutral') || 'neutral'
                    }
                    size="sm"
                  >
                    {dsTypeLabel(ds.dsType)}
                  </Badge>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!editsEnabled}
                  onClick={() => setRemoveConfirm(ds)}
                >
                  Remove
                </Button>
              </DsRow>
            ))
          )}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginTop: '0.75rem',
            }}
          >
            <Button
              variant="outline"
              size="sm"
              disabled={!editsEnabled}
              onClick={() => {
                setPickerSelected(dataSources.map((d) => d.dataSourceId));
                setAssociateOpen(true);
              }}
            >
              + Associate
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!editsEnabled}
              onClick={() =>
                navigate(
                  `/data-sources?create=1&returnTo=${encodeURIComponent(`/agents/${agent.id}`)}&tab=configure`,
                )
              }
            >
              + Create data source
            </Button>
          </div>
          {dataSources.length > 0 && (
            <DsDragTray data-testid="ds-drag-tray">
              <DsDragLabel>
                Drag into Agent Builder
                <span
                  style={{
                    marginLeft: 'auto',
                    fontWeight: 400,
                    color: 'var(--text-muted)',
                    fontSize: '0.6875rem',
                  }}
                >
                  Grip · drag chip → dropzone
                </span>
              </DsDragLabel>
              <ChipRow>
                {dataSources.map((ds) => (
                  <DsChip
                    key={ds.dataSourceId}
                    draggable={editsEnabled}
                    title="Drag into Agent Builder"
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', ds.name);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                  >
                    <Grip aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </Grip>
                    {ds.name}
                  </DsChip>
                ))}
              </ChipRow>
            </DsDragTray>
          )}
        </div>

        <ComingSoon>
          <BlockHeader>Actions</BlockHeader>
          <Helper style={{ margin: 0 }}>
            Integrations coming next — same associate + drag-into-builder
            pattern as data sources.
          </Helper>
        </ComingSoon>

        {pending && (
          <Badge variant="warning" size="sm">
            Pending changes (Accept to apply to Draft)
          </Badge>
        )}
        <Helper>
          Empty fleet is warned on Publish. Changes stay on Draft until you
          publish.
        </Helper>
      </PreviewCard>

      <Modal
        isOpen={associateOpen}
        onClose={() => setAssociateOpen(false)}
        title="Associate data sources"
      >
        <Text variant="body1" style={{ margin: 0, fontSize: '0.875rem' }}>
          Pick from your organisation catalog. Credentials stay on the Data
          Sources wizard — never paste secrets here.
        </Text>
        <PickerList>
          {(orgDataSources || []).map((ds) => (
            <PickerRow key={ds.dataSourceId}>
              <input
                type="checkbox"
                checked={pickerSelected.includes(ds.dataSourceId)}
                onChange={(e) => {
                  setPickerSelected((prev) =>
                    e.target.checked
                      ? [...prev, ds.dataSourceId]
                      : prev.filter((id) => id !== ds.dataSourceId),
                  );
                }}
              />
              <span>
                {ds.name} · {dsTypeLabel(ds.dsType)}
              </span>
            </PickerRow>
          ))}
          {(orgDataSources || []).length === 0 && (
            <Helper>No data sources in this organisation yet.</Helper>
          )}
        </PickerList>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setAssociateOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!editsEnabled || saving}
            onClick={async () => {
              setSaving(true);
              setActionError(null);
              try {
                await persistDataSources(pickerSelected);
                const next = (orgDataSources || [])
                  .filter((d) => pickerSelected.includes(d.dataSourceId))
                  .map((d) => ({
                    dataSourceId: d.dataSourceId,
                    name: d.name,
                    dsType: d.dsType,
                    status: d.status,
                  }));
                setDataSources(next);
                setSavedDataSourceIds(pickerSelected);
                setAssociateOpen(false);
              } catch (err) {
                setActionError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to update data sources',
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            Save associations
          </Button>
        </div>
        {availableToAssociate.length === 0 &&
          (orgDataSources || []).length > 0 && (
            <Helper>All org data sources are already linked.</Helper>
          )}
      </Modal>

      <Modal
        isOpen={Boolean(removeConfirm)}
        onClose={() => setRemoveConfirm(null)}
        title="Remove data source?"
      >
        <Text variant="body1" style={{ margin: '0 0 1rem' }}>
          Unlink <strong>{removeConfirm?.name}</strong> from this agent? You can
          re-associate later.
        </Text>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setRemoveConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving}
            onClick={async () => {
              if (!removeConfirm) return;
              setSaving(true);
              setActionError(null);
              try {
                const nextIds = dataSources
                  .filter((d) => d.dataSourceId !== removeConfirm.dataSourceId)
                  .map((d) => d.dataSourceId);
                await persistDataSources(nextIds);
                const next = dataSources.filter(
                  (d) => d.dataSourceId !== removeConfirm.dataSourceId,
                );
                setDataSources(next);
                setSavedDataSourceIds(nextIds);
                setRemoveConfirm(null);
              } catch (err) {
                setActionError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to remove data source',
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </Layout>
  );
};

export default ConfigureTab;
export { parseBuilderResponse, toProposed, configsDiffer } from '../AgentBuilder/builderHelpers';
